import type { STATION_DETAIL, UpdateStation } from '@/types/station'
import { displayTwoDecimal } from '@/utils/numberUtils'
import { resolveStationName } from '@/utils/stationName'

const trimmedString = (value: unknown) =>
  typeof value === 'string' ? value.trim() : ''

type UnitConverter = (
  value: number,
  metricUnit: string,
  imperialUnit?: string,
) => number

interface EntitySelection {
  id?: string | number | null
}

interface ControllerSelection extends EntitySelection {
  address?: string | number | null
  index?: string | number | null
  name?: unknown
  twiId?: number | null
}

interface StationEditFormValues {
  priority?: number | string | null
  description?: string | null
  image?: string | null
  pictureFileName?: string | null
  grsCoordinatedLatitude?: number | string | null
  grsCoordinatedLongitude?: number | string | null
  floorId?: number | string | null
  controller?: ControllerSelection | null
  plant?: EntitySelection | null
  pipe?: EntitySelection | null
  soil?: EntitySelection | null
  emitter?: EntitySelection | null
  etZone?: EntitySelection | null
  subAreaParent?: EntitySelection | null
  water?: number | string | null
  surface?: number | string | null
  plant_water?: number | string | null
  preciptation?: number | string | null
  expected?: number | string | null
  landscape?: number | string | null
  runtime?: number | string | null
  defaultRunTime?: string | null
  Type?: { value?: number | null } | number | null
}

interface BuildStationUpdatePayloadParams {
  detail: STATION_DETAIL
  formValues: StationEditFormValues
  interfaceId: number
  stationGroupIdFallback: number
  convertFromMetric: UnitConverter
  convertToMetric: UnitConverter
}

export const STATION_TYPE_OPTIONS = [
  { label: 'Non Irrigation', value: 1 },
  { label: 'Irrigation', value: 2 },
]

const convertedDisplayValue = (
  value: number | null | undefined,
  metricUnit: string,
  imperialUnit: string,
  convertFromMetric: UnitConverter,
) => {
  if (value === null || value === undefined) return ''

  return displayTwoDecimal(
    convertFromMetric(value, metricUnit, imperialUnit),
  )
}

export const mapStationDetailToFormValues = (
  detail: STATION_DETAIL,
  convertFromMetric: UnitConverter,
) => {
  const stationType = Number(detail.type ?? detail.stationType)

  return {
    id: detail.id,
    priority: detail.priority,
    description: resolveStationName(detail),
    image: detail.pictureUrl,
    pictureFileName: detail.pictureFileName,
    grsCoordinatedLatitude: detail.grsCoordinatedLatitude,
    grsCoordinatedLongitude: detail.grsCoordinatedLongitude,
    floorId: detail.floorId ?? detail.floor?.id,
    areaParent: {
      id: detail.subArea?.parentArea?.id || 0,
      name: detail.subArea?.parentArea?.name || '',
    },
    subAreaParent: {
      id: detail.subArea?.id || 0,
      name: detail.subArea?.name || '',
    },
    controller: detail.controller
      ? {
          ...detail.controller,
          id: detail.decoderId || detail.controller.id,
          configuration: detail.controller.controllerConfigure,
        }
      : undefined,
    plant: detail.plant,
    pipe: detail.pipe,
    soil: detail.soil,
    emitter: detail.emitter,
    etZone: detail.etZone,
    water: convertedDisplayValue(
      detail.waterRequired,
      'mm',
      'in',
      convertFromMetric,
    ),
    surface:
      detail.surfaceAccumulation === null ||
      detail.surfaceAccumulation === undefined
        ? ''
        : convertFromMetric(
            detail.surfaceAccumulation,
            'mm',
            'in',
          ).toString(),
    preciptation: convertedDisplayValue(
      detail.precipitationRate,
      'mm/h',
      'in/h',
      convertFromMetric,
    ),
    plant_water: convertedDisplayValue(
      detail.plantAvailWater,
      'mm',
      'in',
      convertFromMetric,
    ),
    expected: convertedDisplayValue(
      detail.expectedFlow,
      'l/m',
      'gpm',
      convertFromMetric,
    ),
    landscape: detail.landscapeFactor,
    runtime: detail.rtDayOverride,
    siteId: detail.siteId,
    siteName: detail.siteName,
    locationId: detail.locationId,
    locationName: detail.locationName,
    defaultRunTime: detail.defaultRunTime,
    Type:
      STATION_TYPE_OPTIONS.find((option) => option.value === stationType) ||
      STATION_TYPE_OPTIONS[1],
  }
}

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === ''

const numericValue = (value: unknown) => {
  if (isBlank(value)) return null

  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : parsed
}

const sameNumericValue = (left: unknown, right: unknown) =>
  Object.is(numericValue(left), numericValue(right))

const sameEntity = (
  left: EntitySelection | null | undefined,
  right: EntitySelection | null | undefined,
) => sameNumericValue(left?.id, right?.id)

const nullableNumber = (value: unknown) =>
  isBlank(value) ? null : Number(value)

const selectedType = (value: StationEditFormValues['Type']) =>
  typeof value === 'object' && value !== null ? value.value : value

const editedMeasurement = (
  value: unknown,
  metricUnit: string,
  imperialUnit: string,
  convertToMetric: UnitConverter,
) => {
  if (isBlank(value)) return null

  return convertToMetric(Number(value), metricUnit, imperialUnit)
}

export const buildStationUpdatePayload = ({
  detail,
  formValues,
  interfaceId,
  stationGroupIdFallback,
  convertFromMetric,
  convertToMetric,
}: BuildStationUpdatePayloadParams): UpdateStation => {
  const initialValues = mapStationDetailToFormValues(
    detail,
    convertFromMetric,
  )
  const controllerChanged = !sameEntity(
    formValues.controller,
    initialValues.controller,
  )
  const submittedName = trimmedString(formValues.description)
  const storedName = trimmedString(detail.description)
  const description =
    submittedName === initialValues.description
      ? storedName
        ? detail.description
        : initialValues.description
      : submittedName ||
        resolveStationName({ controller: formValues.controller }) ||
        resolveStationName(detail)
  const measurement = (
    fieldValue: unknown,
    initialValue: unknown,
    rawValue: number | null,
    metricUnit: string,
    imperialUnit: string,
  ) =>
    sameNumericValue(fieldValue, initialValue)
      ? rawValue
      : editedMeasurement(
          fieldValue,
          metricUnit,
          imperialUnit,
          convertToMetric,
        )
  const scalarNumber = (
    fieldValue: unknown,
    initialValue: unknown,
    rawValue: number | null,
  ) =>
    sameNumericValue(fieldValue, initialValue)
      ? rawValue
      : nullableNumber(fieldValue)
  const selectedId = (
    fieldValue: EntitySelection | null | undefined,
    initialValue: EntitySelection | null | undefined,
    rawValue: unknown,
  ) =>
    sameEntity(fieldValue, initialValue)
      ? nullableNumber(rawValue)
      : nullableNumber(fieldValue?.id)
  const type = selectedType(formValues.Type)
  const initialType = selectedType(initialValues.Type)
  const decoderId =
    formValues.controller?.id ?? detail.decoderId ?? detail.controller?.id

  return {
    id: detail.id,
    interfaceId,
    decoderId: isBlank(decoderId) ? undefined : String(decoderId),
    description,
    priority: scalarNumber(
      formValues.priority,
      initialValues.priority,
      detail.priority,
    ) as number,
    pictureUrl:
      formValues.image === initialValues.image
        ? detail.pictureUrl
        : formValues.image || '',
    pictureFileName:
      formValues.pictureFileName === initialValues.pictureFileName
        ? detail.pictureFileName
        : formValues.pictureFileName || '',
    grsCoordinatedLatitude: scalarNumber(
      formValues.grsCoordinatedLatitude,
      initialValues.grsCoordinatedLatitude,
      detail.grsCoordinatedLatitude,
    ),
    grsCoordinatedLongitude: scalarNumber(
      formValues.grsCoordinatedLongitude,
      initialValues.grsCoordinatedLongitude,
      detail.grsCoordinatedLongitude,
    ),
    floorId: scalarNumber(
      formValues.floorId,
      initialValues.floorId,
      detail.floorId ?? detail.floor?.id ?? null,
    ),
    surfaceAccumulation: measurement(
      formValues.surface,
      initialValues.surface,
      detail.surfaceAccumulation,
      'mm',
      'in',
    ),
    waterRequired: measurement(
      formValues.water,
      initialValues.water,
      detail.waterRequired,
      'mm',
      'in',
    ),
    plantAvailWater: measurement(
      formValues.plant_water,
      initialValues.plant_water,
      detail.plantAvailWater,
      'mm',
      'in',
    ),
    precipitationRate: measurement(
      formValues.preciptation,
      initialValues.preciptation,
      detail.precipitationRate,
      'mm/h',
      'in/h',
    ),
    expectedFlow: measurement(
      formValues.expected,
      initialValues.expected,
      detail.expectedFlow,
      'l/m',
      'gpm',
    ),
    landscapeFactor: scalarNumber(
      formValues.landscape,
      initialValues.landscape,
      detail.landscapeFactor,
    ),
    rtDayOverride: scalarNumber(
      formValues.runtime,
      initialValues.runtime,
      detail.rtDayOverride,
    ),
    index: controllerChanged
      ? Number(formValues.controller?.index) || 0
      : Number(detail.index ?? detail.controller?.index) || 0,
    address: controllerChanged
      ? Number(formValues.controller?.address) || 0
      : Number(detail.address ?? detail.controller?.address) || 0,
    plantId: selectedId(
      formValues.plant,
      initialValues.plant,
      detail.plantId ?? detail.plant?.id,
    ),
    pipeId: selectedId(
      formValues.pipe,
      initialValues.pipe,
      detail.pipeId ?? detail.pipe?.id,
    ),
    soilId: selectedId(
      formValues.soil,
      initialValues.soil,
      detail.soilId ?? detail.soil?.id,
    ),
    emitterId: selectedId(
      formValues.emitter,
      initialValues.emitter,
      detail.emitterId ?? detail.emitter?.id,
    ),
    etZoneId: selectedId(
      formValues.etZone,
      initialValues.etZone,
      detail.etZoneId ?? detail.etZone?.id,
    ),
    stationGroupId:
      detail.stationGroupId === undefined
        ? stationGroupIdFallback
        : detail.stationGroupId,
    defaultRunTime:
      formValues.defaultRunTime === initialValues.defaultRunTime
        ? detail.defaultRunTime
        : formValues.defaultRunTime || '00:00:00',
    subAreaId: selectedId(
      formValues.subAreaParent,
      initialValues.subAreaParent,
      detail.subArea?.id,
    ) as number,
    twiId: controllerChanged
      ? formValues.controller?.twiId ?? 0
      : detail.twiId ?? detail.controller?.twiId ?? 0,
    Type: sameNumericValue(type, initialType)
      ? (nullableNumber(detail.type ?? detail.stationType) as number | null)
      : (nullableNumber(type) as number | null),
  }
}
